/**
 * UI Event Logger Service
 * Centralized, semantic event logging for React application
 * 
 * Design Principles:
 * - Semantic events only (no low-level DOM events)
 * - Fire-and-forget (non-blocking)
 * - Automatic batching
 * - Correlation tracking
 * - PII awareness
 */

import { v4 as uuidv4 } from 'uuid';

// Event Categories
export const UIEventCategory = {
    NAVIGATION: 'NAVIGATION',
    FORM: 'FORM',
    API_CALL: 'API_CALL',
    ERROR: 'ERROR',
    INTERACTION: 'INTERACTION',
    SESSION: 'SESSION',
    PERFORMANCE: 'PERFORMANCE',
};

// Event Types (semantic and bounded)
export const UIEventType = {
    // Navigation
    PAGE_VIEW: 'PAGE_VIEW',
    ROUTE_CHANGE: 'ROUTE_CHANGE',

    // Forms
    FORM_START: 'FORM_START',
    FORM_SUBMIT: 'FORM_SUBMIT',
    FORM_ERROR: 'FORM_ERROR',
    FORM_ABANDONED: 'FORM_ABANDONED',
    KASKO_IHBAR_SORGULAMA: 'KASKO_IHBAR_SORGULAMA',
    KASKO_FORM_SUBMIT: 'KASKO_FORM_SUBMIT',

    // API Calls
    API_CALL_START: 'API_CALL_START',
    API_CALL_SUCCESS: 'API_CALL_SUCCESS',
    API_CALL_ERROR: 'API_CALL_ERROR',
    API_CALL_TIMEOUT: 'API_CALL_TIMEOUT',

    // Errors
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    CAPTCHA_FAILED: 'CAPTCHA_FAILED',
    NETWORK_ERROR: 'NETWORK_ERROR',
    UNHANDLED_ERROR: 'UNHANDLED_ERROR',

    // Interactions (minimal)
    BUTTON_CLICK: 'BUTTON_CLICK',
    LINK_CLICK: 'LINK_CLICK',

    // Session
    SESSION_START: 'SESSION_START',
    SESSION_END: 'SESSION_END',
    SESSION_TIMEOUT: 'SESSION_TIMEOUT',

    // Performance
    PAGE_LOAD_TIME: 'PAGE_LOAD_TIME',
    API_RESPONSE_TIME: 'API_RESPONSE_TIME',
};

class UIEventLogger {
    constructor() {
        this.apiBaseUrl = null;
        this.correlationId = this.getOrCreateCorrelationId();
        this.sessionId = this.getOrCreateSessionId();
        this.eventQueue = [];
        this.batchSize = 10;
        this.batchInterval = 5000; // 5 seconds
        this.isEnabled = true;
        this.batchTimer = null;
        
        this.startBatchTimer();
        this.setupBeforeUnloadHandler();
    }

    /**
     * Initialize the logger with API configuration
     */
    initialize(apiBaseUrl) {
        this.apiBaseUrl = apiBaseUrl;
        //this.logSessionStart();
    }

    /**
     * Get or create correlation ID (persists for session)
     */
    getOrCreateCorrelationId() {
        let correlationId = sessionStorage.getItem('ui_correlation_id');
        if (!correlationId) {
            correlationId = uuidv4();
            sessionStorage.setItem('ui_correlation_id', correlationId);
        }
        return correlationId;
    }

    /**
     * Get or create session ID (persists across tabs)
     */
    getOrCreateSessionId() {
        let sessionId = localStorage.getItem('ui_session_id');
        const sessionTimestamp = localStorage.getItem('ui_session_timestamp');
        const now = Date.now();

        // Session expires after 30 minutes of inactivity
        if (!sessionId || !sessionTimestamp || (now - parseInt(sessionTimestamp) > 30 * 60 * 1000)) {
            sessionId = uuidv4();
            localStorage.setItem('ui_session_id', sessionId);
        }

        localStorage.setItem('ui_session_timestamp', now.toString());
        return sessionId;
    }

    /**
     * Main logging method
     */
    log(eventType, eventCategory, metadata = {}) {
        if (!this.isEnabled || !this.apiBaseUrl) {
            return;
        }

        const event = {
            eventType,
            eventCategory,
            correlationId: this.correlationId,
            sessionId: this.sessionId,
            metadata: this.sanitizeMetadata(metadata),
            clientTimestamp: Date.now(),
        };

        this.eventQueue.push(event);

        // Send immediately for critical events
        if (this.isCriticalEvent(eventType)) {
            this.flush();
        } else if (this.eventQueue.length >= this.batchSize) {
            this.flush();
        }
    }

    /**
     * Sanitize metadata to prevent PII leakage
     */
    sanitizeMetadata(metadata) {
        const sanitized = { ...metadata };

        // Remove sensitive fields
        const sensitiveKeys = ['password', 'pin', 'cvv', 'creditCard', 'ssn'];
        sensitiveKeys.forEach(key => {
            if (sanitized[key]) {
                sanitized[key] = '[REDACTED]';
            }
        });

        // Truncate long strings
        Object.keys(sanitized).forEach(key => {
            if (typeof sanitized[key] === 'string' && sanitized[key].length > 500) {
                sanitized[key] = sanitized[key].substring(0, 500) + '...';
            }
        });

        return sanitized;
    }

    /**
     * Check if event is critical (should be sent immediately)
     */
    isCriticalEvent(eventType) {
        const criticalEvents = [
            UIEventType.UNHANDLED_ERROR,
            UIEventType.API_CALL_ERROR,
            UIEventType.SESSION_END,
        ];
        return criticalEvents.includes(eventType);
    }

    /**
     * Send queued events to backend
     */
    async flush() {
        if (this.eventQueue.length === 0 || !this.apiBaseUrl) {
            return;
        }

        const eventsToSend = [...this.eventQueue];
        this.eventQueue = [];

        try {
            const endpoint = eventsToSend.length === 1 
                ? `${this.apiBaseUrl}/UIEventLog/log`
                : `${this.apiBaseUrl}/UIEventLog/log-batch`;

            const body = eventsToSend.length === 1 
                ? eventsToSend[0]
                : eventsToSend;

            // Fire-and-forget (don't block UI)
            fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                keepalive: true, // Important: keeps request alive even if page unloads
            }).catch(err => {
                // Silently fail - logging should never crash the app
                console.debug('UI event log failed:', err);
            });
        } catch (error) {
            console.debug('Failed to send UI events:', error);
        }
    }

    /**
     * Start batch timer
     */
    startBatchTimer() {
        if (this.batchTimer) {
            clearInterval(this.batchTimer);
        }

        this.batchTimer = setInterval(() => {
            this.flush();
        }, this.batchInterval);
    }

    /**
     * Setup handler to flush events before page unload
     */
    setupBeforeUnloadHandler() {
        window.addEventListener('beforeunload', () => {
            //this.logSessionEnd();
            this.flush();
        });
    }

    /**
     * Disable logging (useful for development/debugging)
     */
    disable() {
        this.isEnabled = false;
    }

    /**
     * Enable logging
     */
    enable() {
        this.isEnabled = true;
    }

    // =====================================================
    // Convenience Methods for Common Events
    // =====================================================

    logPageView(pageName, metadata = {}) {
        this.log(UIEventType.PAGE_VIEW, UIEventCategory.NAVIGATION, {
            pageName,
            url: window.location.pathname,
            ...metadata,
        });
    }

    logFormStart(formName, metadata = {}) {
        this.log(UIEventType.FORM_START, UIEventCategory.FORM, {
            formName,
            ...metadata,
        });
    }

    logFormSubmit(formName, metadata = {}) {
        this.log(UIEventType.FORM_SUBMIT, UIEventCategory.FORM, {
            formName,
            ...metadata,
        });
    }

    logFormError(formName, errorMessage, metadata = {}) {
        this.log(UIEventType.FORM_ERROR, UIEventCategory.ERROR, {
            formName,
            errorMessage,
            ...metadata,
        });
    }

    logValidationError(fieldName, errorMessage, metadata = {}) {
        this.log(UIEventType.VALIDATION_ERROR, UIEventCategory.ERROR, {
            fieldName,
            errorMessage,
            ...metadata,
        });
    }

    logCaptchaFailed(metadata = {}) {
        this.log(UIEventType.CAPTCHA_FAILED, UIEventCategory.ERROR, metadata);
    }

    logApiCall(endpoint, method, metadata = {}) {
        this.log(UIEventType.API_CALL_START, UIEventCategory.API_CALL, {
            endpoint,
            method,
            ...metadata,
        });
    }

    logApiSuccess(endpoint, duration, metadata = {}) {
        this.log(UIEventType.API_CALL_SUCCESS, UIEventCategory.API_CALL, {
            endpoint,
            durationMs: duration,
            ...metadata,
        });
    }

    logApiError(endpoint, errorMessage, statusCode, metadata = {}) {
        this.log(UIEventType.API_CALL_ERROR, UIEventCategory.ERROR, {
            endpoint,
            errorMessage,
            statusCode,
            ...metadata,
        });
    }

    logButtonClick(buttonName, metadata = {}) {
        this.log(UIEventType.BUTTON_CLICK, UIEventCategory.INTERACTION, {
            buttonName,
            ...metadata,
        });
    }

    logSessionStart() {
        this.log(UIEventType.SESSION_START, UIEventCategory.SESSION, {
            userAgent: navigator.userAgent,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }

    logSessionEnd() {
        this.log(UIEventType.SESSION_END, UIEventCategory.SESSION, {
            sessionDuration: Date.now() - parseInt(localStorage.getItem('ui_session_timestamp') || Date.now()),
        });
    }

    logUnhandledError(error, metadata = {}) {
        this.log(UIEventType.UNHANDLED_ERROR, UIEventCategory.ERROR, {
            errorMessage: error.message,
            errorStack: error.stack,
            ...metadata,
        });
    }
}

// Singleton instance
const uiEventLogger = new UIEventLogger();

export default uiEventLogger;
